/**
 * Product media gallery: variant-based slide filtering and thumbnail navigation by media ID.
 * Main gallery keeps all slides in the DOM; filtering hides thumbnails only.
 * Navigation always targets slides by media ID and skips filtered-out slides.
 */
window.ProductMediaGallery = {
  mediaMatchesFilterValue(mediaName, value, allFilterValues = []) {
    if (!mediaName || !value) return false;

    const idx = mediaName.indexOf(value);
    if (idx === -1) return false;

    const prevChar = idx > 0 ? mediaName[idx - 1] : null;
    const afterValue = mediaName.substring(idx + value.length);
    const validBoundaryBefore = !prevChar || !/[a-zA-Z0-9]/.test(prevChar);
    const valuesThatExtend = allFilterValues.filter((v) => v !== value && v.startsWith(value));
    const isExtendedByOther = valuesThatExtend.some((v) => afterValue.startsWith(v.substring(value.length)));

    return validBoundaryBefore && !isExtendedByOther;
  },

  getFilterConfig(galleryRoot) {
    const mainEl = galleryRoot.querySelector('[data-media-gallery-swiper]');
    if (!mainEl) return null;

    return {
      galleryRoot,
      mainEl,
      thumbsEl: galleryRoot.querySelector('.js-product-media-gallery-thumbnails'),
      criteria: galleryRoot.dataset.filterCriteria || 'none',
      filterValues: (galleryRoot.dataset.filterValues || '').split(',').filter(Boolean),
      selected: galleryRoot.dataset.filterSelected || '',
    };
  },

  getShouldFilter(config, selected) {
    const { criteria, filterValues } = config;
    const shouldFilter = criteria !== 'none' && filterValues.length > 0 && selected.length > 0;

    if (!shouldFilter) return false;

    const mainSlides = config.mainEl.querySelectorAll('swiper-slide[data-media-name]');
    const hasMatchForSelected = Array.from(mainSlides).some((slide) =>
      this.mediaMatchesFilterValue(slide.getAttribute('data-media-name') || '', selected, filterValues)
    );

    return hasMatchForSelected;
  },

  getSlideFilterInfo(mediaName, selected, filterValues, shouldFilter) {
    if (!shouldFilter) {
      return { show: true, isGeneric: false, isSelectedSpecific: false, owner: null };
    }

    const matchingValues = filterValues.filter((value) =>
      this.mediaMatchesFilterValue(mediaName, value, filterValues)
    );
    const owner = matchingValues.length
      ? matchingValues.reduce((a, b) => (a.length >= b.length ? a : b))
      : null;
    const isGeneric = matchingValues.length === 0;
    const isSelectedSpecific = owner === selected;

    return {
      show: isSelectedSpecific || isGeneric,
      isGeneric,
      isSelectedSpecific,
      owner,
    };
  },

  shouldShowSlide(mediaName, selected, filterValues, shouldFilter) {
    return this.getSlideFilterInfo(mediaName, selected, filterValues, shouldFilter).show;
  },

  setSlideFiltered(slide, hidden) {
    slide.classList.toggle('is-filtered-out', hidden);
  },

  getVisibleMainSlides(mainEl) {
    return Array.from(mainEl.querySelectorAll('swiper-slide[data-media-id]')).filter(
      (slide) => !slide.classList.contains('is-filtered-out')
    );
  },

  getActiveMainSlide(mainEl) {
    const swiper = mainEl.swiper;
    if (!swiper) return null;

    const activeSlide = swiper.slides[swiper.activeIndex];
    if (activeSlide && !activeSlide.classList.contains('is-filtered-out')) {
      return activeSlide;
    }

    return null;
  },

  resolveTargetMainSlide(galleryRoot, selectedValue, preferredMediaId) {
    const config = this.getFilterConfig(galleryRoot);
    if (!config) return null;

    const selected = selectedValue ?? config.selected;
    const { filterValues } = config;
    const shouldFilter = this.getShouldFilter(config, selected);
    const mainSlides = Array.from(
      config.mainEl.querySelectorAll('swiper-slide[data-media-id][data-media-name]')
    );

    const visibleSlides = mainSlides.filter((slide) => {
      const mediaName = slide.getAttribute('data-media-name') || '';
      return this.shouldShowSlide(mediaName, selected, filterValues, shouldFilter);
    });

    const variantSpecific = visibleSlides.find((slide) => {
      const mediaName = slide.getAttribute('data-media-name') || '';
      return this.getSlideFilterInfo(mediaName, selected, filterValues, shouldFilter).isSelectedSpecific;
    });
    if (variantSpecific) return variantSpecific;

    if (preferredMediaId) {
      const preferred = config.mainEl.querySelector(
        `swiper-slide[data-media-id="${preferredMediaId}"]`
      );
      if (preferred && visibleSlides.includes(preferred)) {
        return preferred;
      }
    }

    const generic = visibleSlides.find((slide) => {
      const mediaName = slide.getAttribute('data-media-name') || '';
      return this.getSlideFilterInfo(mediaName, selected, filterValues, shouldFilter).isGeneric;
    });
    if (generic) return generic;

    return visibleSlides[0] || null;
  },

  applyFilter(galleryRoot, selectedValue, options = {}) {
    const { skipSwiperUpdate = false } = options;
    const config = this.getFilterConfig(galleryRoot);
    if (!config) return false;

    if (selectedValue !== undefined) {
      galleryRoot.dataset.filterSelected = selectedValue;
      config.selected = selectedValue;
    }

    const { filterValues, selected, mainEl, thumbsEl } = config;
    const shouldFilter = this.getShouldFilter(config, selected);

    mainEl.querySelectorAll('swiper-slide[data-media-name]').forEach((slide) => {
      const mediaName = slide.getAttribute('data-media-name') || '';
      const show = this.shouldShowSlide(mediaName, selected, filterValues, shouldFilter);
      this.setSlideFiltered(slide, !show);
    });

    if (thumbsEl) {
      thumbsEl.querySelectorAll('swiper-slide[data-media-name]').forEach((slide) => {
        const mediaName = slide.getAttribute('data-media-name') || '';
        const show = this.shouldShowSlide(mediaName, selected, filterValues, shouldFilter);
        this.setSlideFiltered(slide, !show);
      });
    }

    if (!skipSwiperUpdate) {
      this.refreshSwipers(galleryRoot);
    }

    return shouldFilter;
  },

  refreshSwipers(galleryRoot, options = {}) {
    const { main = true, thumbs = true } = options;
    const config = this.getFilterConfig(galleryRoot);
    if (!config) return;

    if (main && config.mainEl.swiper) {
      config.mainEl.swiper.update();
      config.mainEl.swiper.updateAutoHeight?.();
    }

    if (thumbs && config.thumbsEl?.swiper) {
      config.thumbsEl.swiper.update();
    }
  },

  getSlideByMediaId(galleryRoot, mediaId) {
    if (!mediaId) return null;

    const slide = galleryRoot.querySelector(
      `[data-media-gallery-swiper] swiper-slide[data-media-id="${mediaId}"]`
    );

    if (!slide || slide.classList.contains('is-filtered-out')) return null;

    return slide;
  },

  getSwiperIndexForSlide(mainEl, slide) {
    const swiper = mainEl.swiper;
    if (!swiper || !slide) return -1;

    return Array.from(swiper.slides).indexOf(slide);
  },

  slideToSlide(mainEl, slide, speed = 0) {
    if (!mainEl?.swiper || !slide) return false;

    const slideIndex = this.getSwiperIndexForSlide(mainEl, slide);
    if (slideIndex === -1) return false;

    mainEl.swiper.slideTo(slideIndex, speed);
    return true;
  },

  goToMediaId(galleryRoot, mediaId, options = {}) {
    const { speed = 500 } = options;
    const config = this.getFilterConfig(galleryRoot);
    if (!config?.mainEl?.swiper) return false;

    let targetSlide = this.getSlideByMediaId(galleryRoot, mediaId);

    if (!targetSlide) {
      targetSlide = this.getVisibleMainSlides(config.mainEl)[0] || null;
    }

    if (!targetSlide) return false;

    return this.slideToSlide(config.mainEl, targetSlide, speed);
  },

  goToAdjacentVisible(galleryRoot, direction, options = {}) {
    const { speed = 500 } = options;
    const config = this.getFilterConfig(galleryRoot);
    if (!config?.mainEl?.swiper) return false;

    const visibleSlides = this.getVisibleMainSlides(config.mainEl);
    if (visibleSlides.length === 0) return false;

    const activeSlide = this.getActiveMainSlide(config.mainEl);
    const activeMediaId = activeSlide?.dataset?.mediaId;
    let currentIndex = visibleSlides.findIndex((slide) => slide.dataset.mediaId === activeMediaId);

    if (currentIndex === -1) currentIndex = 0;

    const nextIndex = currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= visibleSlides.length) return false;

    return this.slideToSlide(config.mainEl, visibleSlides[nextIndex], speed);
  },

  setActiveThumbnail(galleryRoot, activeThumb) {
    const config = this.getFilterConfig(galleryRoot);
    if (!config?.thumbsEl) return;

    config.thumbsEl.querySelectorAll('swiper-slide[data-media-id]').forEach((thumb) => {
      thumb.classList.toggle('is-active', thumb === activeThumb);
      thumb.toggleAttribute('aria-current', thumb === activeThumb);
    });
  },

  syncActiveThumbnail(galleryRoot) {
    const config = this.getFilterConfig(galleryRoot);
    if (!config?.mainEl?.swiper || !config.thumbsEl) return;

    const activeSlide = this.getActiveMainSlide(config.mainEl);
    if (!activeSlide) return;

    const mediaId = activeSlide.dataset.mediaId;
    const activeThumb = config.thumbsEl.querySelector(
      `swiper-slide[data-media-id="${mediaId}"]:not(.is-filtered-out)`
    );
    this.setActiveThumbnail(galleryRoot, activeThumb || null);
  },

  bindThumbs(galleryRoot) {
    const config = this.getFilterConfig(galleryRoot);
    if (!config?.thumbsEl || config.thumbsEl.dataset.thumbsBound === 'true') return;

    config.thumbsEl.dataset.thumbsBound = 'true';

    config.thumbsEl.querySelectorAll('swiper-slide[data-media-id]').forEach((thumbSlide) => {
      thumbSlide.addEventListener('click', () => {
        if (thumbSlide.classList.contains('is-filtered-out')) return;

        this.goToMediaId(galleryRoot, thumbSlide.dataset.mediaId);
      });
    });
  },

  bindMainEvents(galleryRoot) {
    const config = this.getFilterConfig(galleryRoot);
    if (!config?.mainEl?.swiper || config.mainEl.dataset.mainEventsBound === 'true') return;

    config.mainEl.dataset.mainEventsBound = 'true';
    const swiper = config.mainEl.swiper;

    swiper.on('slideChange', () => {
      this.ensureActiveSlideVisible(galleryRoot);
    });

    swiper.on('slideChangeTransitionEnd', () => {
      this.syncActiveThumbnail(galleryRoot);
    });
  },

  bindMainArrows(galleryRoot) {
    const config = this.getFilterConfig(galleryRoot);
    if (!config || config.mainEl.dataset.mainArrowsBound === 'true') return;

    config.mainEl.dataset.mainArrowsBound = 'true';

    const sectionId = config.mainEl.dataset.sectionId;
    const prevBtn = document.getElementById(`swiper-custom-button-prev--${sectionId}`);
    const nextBtn = document.getElementById(`swiper-custom-button-next--${sectionId}`);

    const handleArrowClick = (event, direction) => {
      const currentConfig = this.getFilterConfig(galleryRoot);
      if (!currentConfig) return;

      const shouldFilter = this.getShouldFilter(currentConfig, currentConfig.selected);
      if (!shouldFilter) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.goToAdjacentVisible(galleryRoot, direction);
    };

    prevBtn?.addEventListener('click', (event) => handleArrowClick(event, -1), true);
    nextBtn?.addEventListener('click', (event) => handleArrowClick(event, 1), true);
  },

  bindVideoAutoplay(galleryRoot) {
    const config = this.getFilterConfig(galleryRoot);
    if (!config?.mainEl?.swiper || config.mainEl.dataset.videoAutoplayBound === 'true') return;

    config.mainEl.dataset.videoAutoplayBound = 'true';

    config.mainEl.swiper.on('realIndexChange', () => {
      const activeSlide = this.getActiveMainSlide(config.mainEl);
      window.pauseAllMedia();
      const deferredMedia = activeSlide?.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    });
  },

  ensureActiveSlideVisible(galleryRoot) {
    const config = this.getFilterConfig(galleryRoot);
    if (!config?.mainEl?.swiper) return;

    const swiper = config.mainEl.swiper;
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (!activeSlide?.classList.contains('is-filtered-out')) return;

    const visibleSlides = this.getVisibleMainSlides(config.mainEl);
    if (visibleSlides.length === 0) return;

    const previousIndex = swiper.previousIndex ?? swiper.activeIndex;
    const direction = swiper.activeIndex >= previousIndex ? 1 : -1;

    const previousSlide = swiper.slides[previousIndex];
    const previousMediaId = previousSlide?.dataset?.mediaId;
    const previousVisibleIndex = visibleSlides.findIndex(
      (slide) => slide.dataset.mediaId === previousMediaId
    );

    let targetSlide = null;

    if (previousVisibleIndex !== -1) {
      const nextIndex = previousVisibleIndex + direction;
      if (nextIndex >= 0 && nextIndex < visibleSlides.length) {
        targetSlide = visibleSlides[nextIndex];
      }
    }

    if (!targetSlide) {
      targetSlide = visibleSlides[0];
    }

    this.slideToSlide(config.mainEl, targetSlide, 0);
  },

  updateGalleryForSelection(galleryRoot, selectedValue, preferredMediaId) {
    const config = this.getFilterConfig(galleryRoot);
    if (!config?.mainEl?.swiper) return;

    this.applyFilter(galleryRoot, selectedValue, { skipSwiperUpdate: true });

    const targetSlide = this.resolveTargetMainSlide(galleryRoot, selectedValue, preferredMediaId);

    if (targetSlide) {
      this.slideToSlide(config.mainEl, targetSlide, 0);
    }

    this.ensureActiveSlideVisible(galleryRoot);
    this.refreshSwipers(galleryRoot, { main: false, thumbs: true });
    this.syncActiveThumbnail(galleryRoot);
  },

  updateForVariant(galleryRoot, variant) {
    if (!galleryRoot || !variant) return;

    const criteria = galleryRoot.dataset.filterCriteria || 'none';
    const selectedValue = criteria === 'sku' ? variant.sku : variant.barcode;

    this.updateGalleryForSelection(galleryRoot, selectedValue || '', variant.featured_media?.id);
  },

  initGallery(galleryRoot) {
    if (!galleryRoot || galleryRoot.dataset.galleryFilterInitialized === 'true') return;

    galleryRoot.dataset.galleryFilterInitialized = 'true';
    this.bindThumbs(galleryRoot);
    this.bindMainEvents(galleryRoot);
    this.bindMainArrows(galleryRoot);
    this.bindVideoAutoplay(galleryRoot);
    this.updateGalleryForSelection(galleryRoot, galleryRoot.dataset.filterSelected);
  },
};
